import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notificationRepository } from "../repositories/notificationRepository";

export const notificationQueryKeys = {
  all: ["notifications"] as const,
  list: (filters?: Record<string, string | number | boolean | undefined>) =>
    [...notificationQueryKeys.all, "list", filters] as const,
};

type NotificationListParams = {
  type?: string;
  scope?: string;
  read?: "all" | "read" | "unread";
  limit?: number;
  cursor?: string;
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
};

export const useNotificationsQuery = (params?: NotificationListParams) => {
  const { enabled = true, ...filters } = params ?? {};
  return useQuery({
    queryKey: notificationQueryKeys.list(filters),
    queryFn: () => notificationRepository.getNotifications(filters),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 60 * 10, // 10 minutes
    refetchOnMount: "always",
    retry: 2,
  });
};

export const useMarkNotificationAsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationRepository.markAsRead(id),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
};

export const useUpdateNotificationReadStatusMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, read }: { id: string; read: boolean }) =>
      notificationRepository.updateReadStatus(id, read),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
};

export const useMarkAllAsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scope?: { terreiroId?: string; mediumId?: string }) =>
      notificationRepository.markAllAsRead(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
};

export const useDeleteNotificationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationRepository.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
};

export const useClearAllNotificationsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scope?: { terreiroId?: string; mediumId?: string }) =>
      notificationRepository.clearAll(scope),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: notificationQueryKeys.all,
      });
    },
  });
};
